/**
 * Browser half of live call listening: decode the server's 8 kHz µ-law frames
 * and play them back gap-free with a small jitter buffer.
 *
 * Kept free of React and of the network so it can be tested directly.
 */

export const LISTEN_SAMPLE_RATE = 8000;
/** How far ahead of "now" playback is scheduled: absorbs network jitter. */
export const JITTER_BUFFER_S = 0.25;
/** If the schedule has drifted this far ahead (a stall, a backgrounded tab), catch up. */
export const MAX_LAG_S = 1.2;

const DECODE = new Float32Array(256);
for (let i = 0; i < 256; i += 1) {
  const u = ~i & 0xff;
  const sign = u & 0x80;
  const exponent = (u >> 4) & 0x07;
  const mantissa = u & 0x0f;
  const magnitude = (((mantissa << 3) + 0x84) << exponent) - 0x84;
  DECODE[i] = (sign ? -magnitude : magnitude) / 32768;
}

/** µ-law bytes → linear samples in [-1, 1). */
export function decodeUlaw(bytes: Uint8Array): Float32Array {
  const out = new Float32Array(bytes.length);
  for (let i = 0; i < bytes.length; i += 1) out[i] = DECODE[bytes[i]];
  return out;
}

export function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Linear resample, for browsers whose AudioContext will not run at 8 kHz. */
export function resample(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate === toRate) return input;
  const outLength = Math.max(1, Math.round((input.length * toRate) / fromRate));
  const out = new Float32Array(outLength);
  const step = (input.length - 1) / Math.max(1, outLength - 1);
  for (let i = 0; i < outLength; i += 1) {
    const pos = i * step;
    const left = Math.floor(pos);
    const right = Math.min(input.length - 1, left + 1);
    const frac = pos - left;
    out[i] = input[left] * (1 - frac) + input[right] * frac;
  }
  return out;
}

/**
 * When the next frame should start: right after the previous one, but never
 * sooner than the jitter buffer allows and never so far behind that the
 * listener hears the call seconds late.
 */
export function nextStartTime(scheduledEnd: number, now: number): number {
  if (scheduledEnd < now + JITTER_BUFFER_S * 0.4) return now + JITTER_BUFFER_S;
  if (scheduledEnd > now + MAX_LAG_S) return now + JITTER_BUFFER_S;
  return scheduledEnd;
}

export interface ListenPlayer {
  play: (b64: string) => void;
  setVolume: (value: number) => void;
  close: () => Promise<void>;
  readonly sampleRate: number;
}

/** Must be created from a user gesture (a click), or the browser keeps it silent. */
export function createListenPlayer(): ListenPlayer {
  const AudioContextCtor: typeof AudioContext =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  let ctx: AudioContext;
  try {
    ctx = new AudioContextCtor({ sampleRate: LISTEN_SAMPLE_RATE });
  } catch {
    ctx = new AudioContextCtor();
  }
  void ctx.resume?.();
  const gain = ctx.createGain();
  gain.connect(ctx.destination);
  let scheduledEnd = 0;

  return {
    get sampleRate() {
      return ctx.sampleRate;
    },
    play(b64: string) {
      const samples = resample(decodeUlaw(base64ToBytes(b64)), LISTEN_SAMPLE_RATE, ctx.sampleRate);
      const buffer = ctx.createBuffer(1, samples.length, ctx.sampleRate);
      buffer.getChannelData(0).set(samples);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(gain);
      const start = nextStartTime(scheduledEnd, ctx.currentTime);
      source.start(start);
      scheduledEnd = start + buffer.duration;
    },
    setVolume(value: number) {
      gain.gain.value = Math.max(0, Math.min(1, value));
    },
    async close() {
      try {
        await ctx.close();
      } catch {
        /* already closed */
      }
    },
  };
}
